import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    fuel: 'M3 2v10M3 2h18M21 2v10M3 12h18M12 22V12M17 22h-10', // Placeholder
    tool: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
    dollar: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    settings: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12'
};

export default function VehicleCostAnalysis({ user, operatorDetails }) {
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [fleetLoading, setFleetLoading] = useState(true);

    // Quick Entry States
    const [activeModal, setActiveModal] = useState(null); // 'fuel', 'job', 'part'
    const [quickForm, setQuickForm] = useState({});
    const [dropdownData, setDropdownData] = useState({ workshops: [], services: [], fuelTypes: [], statuses: [] });

    const operatorId = user?.operatorId || user?._id || 'all';
    const country = operatorDetails?.country;

    useEffect(() => {
        fetchVehicles();
        loadDropdownData();
    }, [operatorId]);

    const fetchVehicles = async () => {
        try {
            const res = await axios.get(`${API}/vehicles/${operatorId}`);
            setVehicles(res.data);
        } catch (err) {
            console.error('Fetch vehicles error:', err);
        } finally {
            setFleetLoading(false);
        }
    };

    const loadDropdownData = async () => {
        try {
            const [wRes, mRes] = await Promise.all([
                axios.get(`${API}/workshops/${operatorId}`),
                axios.get(`${API}/master-data`)
            ]);
            setDropdownData({
                workshops: wRes.data,
                services: mRes.data.filter(d => d.category === 'service-types'),
                fuelTypes: mRes.data.filter(d => d.category === 'fuel-types'),
                statuses: mRes.data.filter(d => d.category === 'general-status')
            });
        } catch (err) {
            console.error('Error loading dropdown data:', err);
        }
    };

    useEffect(() => {
        if (selectedVehicle) {
            fetchAnalysis(selectedVehicle._id);
        } else {
            setAnalysis(null);
        }
    }, [selectedVehicle]);

    const fetchAnalysis = async (vehicleId) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/vehicle-analysis/${vehicleId}`);
            setAnalysis(res.data);
        } catch (err) {
            console.error('Fetch analysis error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSave = async (e) => {
        e.preventDefault();
        try {
            let endpoint = '';
            const payload = { ...quickForm, vehicleId: selectedVehicle._id, operatorId };

            if (activeModal === 'fuel') {
                endpoint = '/fuel-records';
            } else if (activeModal === 'job') {
                endpoint = '/maintenance-jobs';
            } else if (activeModal === 'part') {
                endpoint = '/tyre-batteries';
            }

            await axios.post(`${API}${endpoint}`, payload);
            setActiveModal(null);
            setQuickForm({});
            fetchAnalysis(selectedVehicle._id);
        } catch (err) {
            console.error('Quick save error:', err);
            alert('Failed to save record.');
        }
    };

    const openModal = (type) => {
        setActiveModal(type);
        if (type === 'fuel') {
            setQuickForm({
                date: new Date().toISOString().split('T')[0],
                fuelType: dropdownData.fuelTypes[0]?.name || '',
                quantity: '', cost: '', mileage: ''
            });
        } else if (type === 'job') {
            setQuickForm({
                jobName: '',
                maintenanceDate: new Date().toISOString().split('T')[0],
                serviceType: dropdownData.services[0]?.name || '',
                workshopId: dropdownData.workshops[0]?._id || '',
                estimatedCost: '',
                status: dropdownData.statuses.find(s => s.name.toLowerCase().includes('pending'))?.name || dropdownData.statuses[0]?.name || '',
                notes: ''
            });
        } else if (type === 'part') {
            setQuickForm({
                itemName: '',
                type: 'Tyre',
                quantity: 1,
                unitPrice: '',
                totalPrice: '',
                condition: 'New',
                notes: ''
            });
        }
    };

    const costBreakdown = useMemo(() => {
        if (!analysis) return [];
        const { summary } = analysis;
        const total = (summary.fuelCost || 0) + (summary.maintenanceCost || 0) + (summary.partsCost || 0) + (summary.otherCost || 0) + (summary.fixedMonthlyCost || 0);
        if (total === 0) return [];

        return [
            { label: 'Fuel', value: summary.fuelCost, color: '#f59e0b', p: (summary.fuelCost / total) * 100 },
            { label: 'Maintenance', value: summary.maintenanceCost, color: '#6366f1', p: (summary.maintenanceCost / total) * 100 },
            { label: 'Tyres & Parts', value: summary.partsCost, color: '#059669', p: (summary.partsCost / total) * 100 },
            { label: 'Others', value: summary.otherCost, color: '#dc2626', p: (summary.otherCost / total) * 100 },
            { label: 'Fixed Monthly', value: summary.fixedMonthlyCost, color: '#0ea5e9', p: ((summary.fixedMonthlyCost || 0) / total) * 100 }
        ].filter(i => i.value > 0);
    }, [analysis]);

    return (
        <div className="vca-page">
            <style>{`
                .vca-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Outfit', sans-serif; }
                .vca-header { margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-start; }
                .vca-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .vca-subtitle { font-size: 14px; color: #64748b; margin: 0; }

                .vca-selector {
                    width: 350px; background: white; border-radius: 16px; padding: 20px;
                    border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                }
                .vca-select-label { font-size: 12px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px; display: block; }
                .vca-dropdown {
                    width: 100%; padding: 12px 16px; border-radius: 12px; border: 1.5px solid #e2e8f0;
                    font-size: 15px; font-weight: 600; color: #1e293b; outline: none; appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: right 12px center; padding-right: 40px;
                }
                .vca-dropdown:focus { border-color: #6366f1; }

                .vca-grid { display: grid; grid-template-columns: 1fr 320px; gap: 24px; margin-top: 24px; }
                
                .vca-stat-cards { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin-bottom: 24px; }
                .vca-stat-card {
                    background: white; border-radius: 20px; padding: 24px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .vca-stat-val { font-size: 24px; font-weight: 800; color: #0f172a; margin-bottom: 4px; }
                .vca-stat-label { font-size: 13px; font-weight: 600; color: #64748b; }

                .vca-main-card {
                    background: white; border-radius: 24px; border: 1px solid #e2e8f0; overflow: hidden;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05);
                }
                .vca-card-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .vca-card-title { font-size: 18px; font-weight: 700; color: #1e293b; }

                .vca-quick-actions { display: flex; gap: 12px; margin-bottom: 24px; }
                .vca-quick-btn {
                    flex: 1; display: flex; align-items: center; justify-content: center; gap: 10px;
                    background: white; border: 1.5px solid #e2e8f0; padding: 16px; border-radius: 16px;
                    font-weight: 800; color: #1e293b; font-size: 14px; cursor: pointer; transition: all 0.2s;
                }
                .vca-quick-btn:hover { background: #f8fafc; border-color: #6366f1; transform: translateY(-2px); box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
                .vca-quick-btn i { color: #6366f1; }

                .vca-timeline { padding: 24px; }
                .vca-log-item { display: flex; gap: 16px; padding: 16px; border-radius: 16px; transition: background 0.2s; border: 1px solid transparent; }
                .vca-log-item:hover { background: #f8fafc; border-color: #f1f5f9; }
                .vca-log-icon {
                    width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    flex-shrink: 0;
                }
                .vca-log-info { flex: 1; min-width: 0; }
                .vca-log-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; }
                .vca-log-type { font-weight: 700; color: #1e293b; font-size: 15px; }
                .vca-log-amount { font-weight: 800; color: #0f172a; font-size: 15px; }
                .vca-log-notes { font-size: 13px; color: #64748b; line-height: 1.5; }
                .vca-log-date { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-top: 6px; }

                .vca-sidebar-card { background: white; border-radius: 20px; padding: 24px; border: 1px solid #e2e8f0; position: sticky; top: 30px; }
                
                .vca-chart-container { margin-top: 24px; }
                .vca-chart-bar { height: 12px; background: #f1f5f9; border-radius: 6px; overflow: hidden; display: flex; margin-bottom: 24px; }
                .vca-chart-segment { height: 100%; transition: width 0.5s ease-out; }
                
                .vca-legend-item { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
                .vca-legend-left { display: flex; align-items: center; gap: 10px; }
                .vca-legend-dot { width: 8px; height: 8px; border-radius: 50%; }
                .vca-legend-label { font-size: 13px; font-weight: 600; color: #475569; }
                .vca-legend-val { font-size: 13px; font-weight: 700; color: #1e293b; }

                .vca-empty {
                    text-align: center; padding: 100px 40px; background: white; border-radius: 30px;
                    border: 2px dashed #e2e8f0; color: #94a3b8; margin-top: 20px;
                }
                .vca-empty-icon { margin-bottom: 16px; opacity: 0.3; }
                .vca-empty-text { font-size: 16px; font-weight: 600; }
                
                /* Modal Styles */
                .vca-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 2000; }
                .vca-modal { background: white; width: 450px; border-radius: 24px; padding: 32px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); position: relative; }
                .vca-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                .vca-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .vca-form-group { margin-bottom: 16px; display: flex; flex-direction: column; gap: 6px; }
                .vca-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
                .vca-input, .vca-select { padding: 12px; border: 1.5px solid #e2e8f0; border-radius: 12px; outline: none; font-size: 14px; font-weight: 600; }
                .vca-input:focus, .vca-select:focus { border-color: #6366f1; }
                .vca-modal-footer { display: flex; gap: 12px; margin-top: 24px; }
                .vca-btn { flex: 1; padding: 12px; border-radius: 12px; font-weight: 700; cursor: pointer; border: none; }
                .vca-btn-save { background: #6366f1; color: white; }
                .vca-btn-cancel { background: #f1f5f9; color: #475569; }

                .loader { border: 3px solid #f3f3f3; border-top: 3px solid #6366f1; border-radius: 50%; width: 24px; height: 24px; animation: spin 1s linear infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            <div className="vca-header">
                <div>
                    <h1 className="vca-title">Vehicle Cost Analysis</h1>
                    <p className="vca-subtitle">Deep financial dive into individual vehicle performance and spending.</p>
                </div>
                <div className="vca-selector">
                    <label className="vca-select-label">Select Vehicle to Analyze</label>
                    <select
                        className="vca-dropdown"
                        value={selectedVehicle?._id || ''}
                        onChange={(e) => {
                            const v = vehicles.find(v => v._id === e.target.value);
                            setSelectedVehicle(v);
                        }}
                    >
                        <option value="">Choose a vehicle...</option>
                        {vehicles.map(v => (
                            <option key={v._id} value={v._id}>
                                {v.plateNumber} — {v.make} {v.model}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {!selectedVehicle ? (
                <div className="vca-empty">
                    <div className="vca-empty-icon"><Icon d={icons.truck} size={48} /></div>
                    <div className="vca-empty-text">Select a vehicle from the dropdown above to view its cost breakdown.</div>
                </div>
            ) : loading ? (
                <div className="vca-empty">
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}><div className="loader"></div></div>
                    <div className="vca-empty-text">Analyzing financial records...</div>
                </div>
            ) : analysis ? (
                <>
                    <div className="vca-quick-actions">
                        <button className="vca-quick-btn" onClick={() => openModal('fuel')}>
                            <Icon d={icons.fuel} size={20} color="#f59e0b" /> Record Fuel
                        </button>
                        <button className="vca-quick-btn" onClick={() => openModal('job')}>
                            <Icon d={icons.tool} size={20} color="#6366f1" /> Maint. Job
                        </button>
                        <button className="vca-quick-btn" onClick={() => openModal('part')}>
                            <Icon d={icons.settings} size={20} color="#059669" /> Tyres/Parts
                        </button>
                    </div>

                    <div className="vca-stat-cards">
                        <div className="vca-stat-card">
                            <div className="vca-stat-val">{formatCurrency(analysis.summary.fuelCost, country)}</div>
                            <div className="vca-stat-label">Total Fuel Cost</div>
                        </div>
                        <div className="vca-stat-card">
                            <div className="vca-stat-val">{formatCurrency(analysis.summary.maintenanceCost, country)}</div>
                            <div className="vca-stat-label">Maintenance Cost</div>
                        </div>
                        <div className="vca-stat-card">
                            <div className="vca-stat-val">{formatCurrency(analysis.summary.partsCost, country)}</div>
                            <div className="vca-stat-label">Tyres & Parts</div>
                        </div>
                        <div className="vca-stat-card">
                            <div className="vca-stat-val" style={{ color: '#0ea5e9' }}>
                                {formatCurrency(analysis.summary.fixedMonthlyCost || 0, country)}
                            </div>
                            <div className="vca-stat-label">Fixed Monthly Costs</div>
                            {analysis.summary.fixedCostBreakdown && (
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, lineHeight: 1.6 }}>
                                    {analysis.summary.fixedCostBreakdown.depreciation > 0 && <div>Depr: {formatCurrency(analysis.summary.fixedCostBreakdown.depreciation, country)}</div>}
                                    {analysis.summary.fixedCostBreakdown.gps > 0 && <div>GPS: {formatCurrency(analysis.summary.fixedCostBreakdown.gps, country)}</div>}
                                    {analysis.summary.fixedCostBreakdown.dashcam > 0 && <div>Dashcam: {formatCurrency(analysis.summary.fixedCostBreakdown.dashcam, country)}</div>}
                                    {analysis.summary.fixedCostBreakdown.insurance > 0 && <div>Insurance: {formatCurrency(analysis.summary.fixedCostBreakdown.insurance, country)}</div>}
                                </div>
                            )}
                        </div>
                        <div className="vca-stat-card">
                            <div className="vca-stat-val" style={{ color: '#6366f1' }}>
                                {formatCurrency((analysis.summary.fuelCost || 0) + (analysis.summary.maintenanceCost || 0) + (analysis.summary.partsCost || 0) + (analysis.summary.otherCost || 0) + (analysis.summary.fixedMonthlyCost || 0), country)}
                            </div>
                            <div className="vca-stat-label">Total Spent to Date</div>
                        </div>
                    </div>

                    <div className="vca-grid">
                        <div className="vca-main-card">
                            <div className="vca-card-header">
                                <h3 className="vca-card-title">Expense Timeline</h3>
                                <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>{analysis.logs.length} Transactions</div>
                            </div>
                            <div className="vca-timeline">
                                {analysis.logs.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No records found for this vehicle.</div>
                                ) : (
                                    analysis.logs.map((log, i) => (
                                        <div key={i} className="vca-log-item">
                                            <div className="vca-log-icon" style={{
                                                background: log.icon === 'fuel' ? '#fff7ed' : log.icon === 'tool' ? '#eef2ff' : log.icon === 'settings' ? '#ecfdf5' : '#fef2f2',
                                                color: log.icon === 'fuel' ? '#f59e0b' : log.icon === 'tool' ? '#6366f1' : log.icon === 'settings' ? '#059669' : '#dc2626'
                                            }}>
                                                <Icon d={icons[log.icon]} size={18} />
                                            </div>
                                            <div className="vca-log-info">
                                                <div className="vca-log-top">
                                                    <span className="vca-log-type">{log.type}</span>
                                                    <span className="vca-log-amount">{formatCurrency(log.amount, country)}</span>
                                                </div>
                                                <div className="vca-log-notes">{log.notes}</div>
                                                <div className="vca-log-date">{new Date(log.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="vca-sidebar">
                            <div className="vca-sidebar-card">
                                <h3 className="vca-card-title" style={{ fontSize: 16 }}>Cost Breakdown</h3>

                                <div className="vca-chart-container">
                                    <div className="vca-chart-bar">
                                        {costBreakdown.map((item, i) => (
                                            <div key={i} className="vca-chart-segment" style={{ width: `${item.p}%`, background: item.color }} />
                                        ))}
                                    </div>

                                    <div className="vca-legend">
                                        {costBreakdown.map((item, i) => (
                                            <div key={i} className="vca-legend-item">
                                                <div className="vca-legend-left">
                                                    <div className="vca-legend-dot" style={{ background: item.color }} />
                                                    <span className="vca-legend-label">{item.label}</span>
                                                </div>
                                                <span className="vca-legend-val">{Math.round(item.p)}%</span>
                                            </div>
                                        ))}
                                        {costBreakdown.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No data to display</div>}
                                    </div>
                                </div>

                                <div style={{ marginTop: 30, padding: 20, background: '#f8fafc', borderRadius: 16, border: '1px dashed #e2e8f0' }}>
                                    <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#1e293b' }}>Vehicle Info</h4>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Plate: <strong>{selectedVehicle.plateNumber}</strong></div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Make: <strong>{selectedVehicle.make} {selectedVehicle.model}</strong></div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>Status: <span style={{ color: '#059669', fontWeight: 700 }}>{selectedVehicle.vehicleStatus}</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            ) : null}

            {/* Quick Entry Modals */}
            {activeModal && (
                <div className="vca-modal-overlay" onClick={() => setActiveModal(null)}>
                    <div className="vca-modal" onClick={e => e.stopPropagation()}>
                        <div className="vca-modal-close" onClick={() => setActiveModal(null)}><Icon d={icons.x} size={20} /></div>
                        <h2 className="vca-modal-title">Record {activeModal === 'fuel' ? 'Fuel' : activeModal === 'job' ? 'Maintenance Job' : 'Tyres / Battery'}</h2>
                        <form onSubmit={handleQuickSave}>
                            {activeModal === 'fuel' && (
                                <>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Date</label>
                                        <input type="date" className="vca-input" value={quickForm.date} onChange={e => setQuickForm({ ...quickForm, date: e.target.value })} />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Fuel Type</label>
                                        <select className="vca-select" value={quickForm.fuelType} onChange={e => setQuickForm({ ...quickForm, fuelType: e.target.value })}>
                                            {dropdownData.fuelTypes.map(f => <option key={f._id} value={f.name}>{f.name}</option>)}
                                        </select>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div className="vca-form-group">
                                            <label className="vca-label">Quantity (L)</label>
                                            <input type="number" className="vca-input" value={quickForm.quantity} onChange={e => setQuickForm({ ...quickForm, quantity: e.target.value })} placeholder="0" />
                                        </div>
                                        <div className="vca-form-group">
                                            <label className="vca-label">Cost</label>
                                            <input type="number" className="vca-input" value={quickForm.cost} onChange={e => setQuickForm({ ...quickForm, cost: e.target.value })} placeholder="0.00" />
                                        </div>
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Current Mileage (km)</label>
                                        <input type="number" className="vca-input" value={quickForm.mileage} onChange={e => setQuickForm({ ...quickForm, mileage: e.target.value })} placeholder="e.g. 15000" />
                                    </div>
                                </>
                            )}

                            {activeModal === 'job' && (
                                <>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Job Name</label>
                                        <input className="vca-input" placeholder="e.g. Engine Oil Change" value={quickForm.jobName} onChange={e => setQuickForm({ ...quickForm, jobName: e.target.value })} />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Workshop</label>
                                        <select className="vca-select" value={quickForm.workshopId} onChange={e => setQuickForm({ ...quickForm, workshopId: e.target.value })}>
                                            <option value="">Select Workshop...</option>
                                            {dropdownData.workshops.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Estimated Cost</label>
                                        <input type="number" className="vca-input" value={quickForm.estimatedCost} onChange={e => setQuickForm({ ...quickForm, estimatedCost: e.target.value })} placeholder="0.00" />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Status</label>
                                        <select className="vca-select" value={quickForm.status} onChange={e => setQuickForm({ ...quickForm, status: e.target.value })}>
                                            {dropdownData.statuses.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                                        </select>
                                    </div>
                                </>
                            )}

                            {activeModal === 'part' && (
                                <>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Item Name</label>
                                        <input className="vca-input" placeholder="e.g. Michelin X Multi" value={quickForm.itemName} onChange={e => setQuickForm({ ...quickForm, itemName: e.target.value })} />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Quantity</label>
                                        <input type="number" className="vca-input" value={quickForm.quantity} onChange={e => setQuickForm({ ...quickForm, quantity: e.target.value })} />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Total Price</label>
                                        <input type="number" className="vca-input" value={quickForm.totalPrice} onChange={e => setQuickForm({ ...quickForm, totalPrice: e.target.value })} placeholder="0.00" />
                                    </div>
                                    <div className="vca-form-group">
                                        <label className="vca-label">Condition</label>
                                        <select className="vca-select" value={quickForm.condition} onChange={e => setQuickForm({ ...quickForm, condition: e.target.value })}>
                                            <option value="New">New</option>
                                            <option value="Used">Used</option>
                                            <option value="Refurbished">Refurbished</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className="vca-modal-footer">
                                <button type="button" className="vca-btn vca-btn-cancel" onClick={() => setActiveModal(null)}>Cancel</button>
                                <button type="submit" className="vca-btn vca-btn-save">Save Record</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
